import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
import { privacyRequestTypes, usStates } from '../data/statePrivacyLaws';
export function DoNotSellMyInfoPage() {
  usePageMeta('Your Privacy Choices | findonlinecollege.com', 'Opt out of the sale or sharing of your personal information and targeted advertising, or request access, correction, deletion, or a copy of your data.');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    requestType: 'opt-out',
    fullName: '',
    email: '',
    phone: '',
    state: '',
    note: ''
  });
  const requestLabel = privacyRequestTypes.find(t => t.value === formData.requestType)?.label || 'Privacy request';
  const isOptOut = formData.requestType === 'opt-out';
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Send the request to the admin inbox via the visitor's email client.
    const subject = `Privacy Rights Request — ${requestLabel} (${formData.state})`;
    const body = ['A new privacy rights request has been submitted:', '', `Request Type: ${requestLabel}`, `Full Name: ${formData.fullName}`, `Email: ${formData.email}`, `Phone: ${formData.phone || 'N/A'}`, `State of Residence: ${formData.state}`, `Additional Notes: ${formData.note || 'N/A'}`].join('\n');
    const mailtoUrl = `mailto:admin@media-matchers.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
    setIsSubmitted(true);
  };
  return <main className="bg-white py-10 sm:py-16" data-id="element-1373" data-mp="2kw4wzk4gi23t">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-800" data-id="element-1374" data-mp="2kxfzzhq2v5tv">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-navy mb-8 transition-colors" data-id="element-1375" data-mp="o20g3cabisgx">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-1376" data-mp="1293pcb04a2k1" />
          Back to home
        </Link>

        {/* Page header */}
        <header className="border-b border-gray-200 pb-8 mb-12" data-id="element-1377" data-mp="16zttjkwt3zd5">
          <h1 className="text-4xl sm:text-5xl font-bold text-navy tracking-tight leading-tight" data-id="element-1378" data-mp="t85kjqhutr0e">
            Your Privacy Choices
          </h1>
          <p className="mt-3 text-lg text-gray-600" data-id="element-1379" data-mp="y8bcz8vy7hjj">
            Do Not Sell or Share My Personal Information
          </p>
        </header>

        {/* Intro */}
        <div className="space-y-5 leading-relaxed text-base mb-12" data-id="element-1380" data-mp="1py65tfj0lpcy">
          <p data-id="element-1381" data-mp="1pwbu8rtmv9lj">
            California and many other states — including Colorado, Connecticut,
            Delaware, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana,
            Nebraska, Nevada, New Hampshire, New Jersey, Oregon, Rhode Island,
            Tennessee, Texas, Utah, and Virginia — give residents rights over
            their personal information. We honor these requests for everyone,
            wherever you live.
          </p>
          <p data-id="element-1382" data-mp="3u3odhyrda16g">
            <strong data-id="element-1383" data-mp="3u34vyj5mpesy">What does "sale" or "sharing" mean?</strong> We do not sell
            your personal information for money. However, we may share your
            information with educational partners to fulfill your requests for
            information about degree programs, or with advertising partners to
            deliver targeted advertisements. Under many state laws, these
            activities may be considered a "sale," "sharing," or "targeted
            advertising."
          </p>
          <p data-id="element-1384" data-mp="36g6hts4zeiy7">
            Use the form below to opt out or to request access to, a copy of,
            correction of, or deletion of your data. You can also email us at{' '}
            <a href="mailto:contact@findonlinecollege.com?subject=Privacy%20Rights%20Request" className="text-navy underline hover:text-crimson" data-id="element-1385" data-mp="3ff1sbgf26f7b">
              contact@findonlinecollege.com
            </a>{' '}
            with the subject line "Privacy Rights Request," or turn on{' '}
            <a href="https://globalprivacycontrol.org/" target="_blank" rel="noopener noreferrer" className="text-navy underline hover:text-crimson" data-id="element-1386" data-mp="30exxq9hft3il">
              Global Privacy Control
            </a>{' '}
            in your browser to opt out automatically.
          </p>
          <p data-id="element-1387" data-mp="3j44ie21sk571">
            For more details on how we handle your data, please review our{' '}
            <Link to="/privacy-policy" className="text-navy underline hover:text-crimson" data-id="element-1388" data-mp="3j6gz6bvtc9ii">
              Privacy Policy
            </Link>
            .
          </p>
        </div>

        {/* Form Section */}
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 sm:p-10" data-id="element-1389" data-mp="1uo7o1iwhyiw">
          {isSubmitted ? <div className="text-center py-8" data-id="element-1390" data-mp="1ypu4os8wwkud">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6" data-id="element-1391" data-mp="23n2laoeewgti">
                <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-1392" data-mp="ksrbhyucjelc" />
              </div>
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-1393" data-mp="g1rfcvt1x0jw">
                Request Received
              </h2>
              <p className="text-gray-600 max-w-md mx-auto" data-id="element-1394" data-mp="bz4p2zc13not">
                {isOptOut ? 'Thank you. We have received your request to opt out of the sale or sharing of your personal information and targeted advertising. We will process it within 15 business days.' : 'Thank you. We will verify your identity using the details you provided and respond within 45 days. If we need more time, we will let you know why.'}
              </p>
            </div> : <>
              <h2 className="text-2xl font-bold text-navy mb-6" data-id="element-1395" data-mp="pqj8h79ulqwe">
                Privacy Request Form
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1396" data-mp="3fejsvittd11i">
                <div data-id="element-1397" data-mp="3ags2it8thmzf">
                  <label htmlFor="requestType" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1398" data-mp="3ag8kzdn2x0lx">
                    What would you like to do?{' '}
                    <span className="text-crimson" data-id="element-1399" data-mp="3gvfsg465g8sq">*</span>
                  </label>
                  <select id="requestType" required value={formData.requestType} onChange={e => setFormData({
                ...formData,
                requestType: e.target.value
              })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-1400" data-mp="32xr8cyjwp5oq">
                    {privacyRequestTypes.map(type => <option key={type.value} value={type.value} data-id="element-1401" data-mp="2a9vb9sqfdbrf">
                        {type.label}
                      </option>)}
                  </select>
                </div>

                <div data-id="element-1402" data-mp="1vmwnq8uov1hz">
                  <label htmlFor="fullName" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1403" data-mp="1vohh3v59oee9">
                    Full Name <span className="text-crimson" data-id="element-1404" data-mp="25mf289rtxv2n">*</span>
                  </label>
                  <input type="text" id="fullName" required value={formData.fullName} onChange={e => setFormData({
                ...formData,
                fullName: e.target.value
              })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-1405" data-mp="20ky60rkaiwse" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1406" data-mp="2taomv8db4cs7">
                  <div data-id="element-1407" data-mp="2t8llxtam8z8p">
                    <label htmlFor="email" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1408" data-mp="3k8kgrlc6vsr">
                      Email Address <span className="text-crimson" data-id="element-1409" data-mp="8kw8r5i6kyja">*</span>
                    </label>
                    <input type="email" id="email" required value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-1410" data-mp="dg1tw23re96b" />
                  </div>
                  <div data-id="element-1411" data-mp="2y7vktkcim0ad">
                    <label htmlFor="phone" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1412" data-mp="2y6ki7nut2yku">
                      Phone Number{' '}
                      <span className="text-gray-500 font-normal" data-id="element-1413" data-mp="2prd549enypav">
                        (Optional)
                      </span>
                    </label>
                    <input type="tel" id="phone" value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-1414" data-mp="2lo8307yhwpmr" />
                  </div>
                </div>

                <div data-id="element-1415" data-mp="1t1dl9it2c4cz">
                  <label htmlFor="state" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1416" data-mp="1szstg0y4uzit">
                    State of Residence <span className="text-crimson" data-id="element-1417" data-mp="21v8u5ps4cxhy">*</span>
                  </label>
                  <select id="state" required value={formData.state} onChange={e => setFormData({
                ...formData,
                state: e.target.value
              })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-navy" data-id="element-1418" data-mp="1xuyqijrsuj69">
                    <option value="" data-id="element-1419" data-mp="1svm3nz0xhc3o">Select a state</option>
                    {usStates.map(state => <option key={state} value={state} data-id="element-1420" data-mp="1nv7tobfrns30">
                        {state}
                      </option>)}
                    <option value="Outside the U.S." data-id="element-1421" data-mp="34clt8mvw5cu0">Outside the U.S.</option>
                  </select>
                </div>

                <div data-id="element-1422" data-mp="l3wyezkbu1sl">
                  <label htmlFor="note" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1423" data-mp="l3doo64tyjpn">
                    Additional Notes{' '}
                    <span className="text-gray-500 font-normal" data-id="element-1424" data-mp="c3qpzs3hjs0a">
                      (Optional)
                    </span>
                  </label>
                  <textarea id="note" rows={4} value={formData.note} onChange={e => setFormData({
                ...formData,
                note: e.target.value
              })} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy resize-none" placeholder="Please provide any additional details regarding your request..." data-id="element-1425" data-mp="h462khfy0vh7" />
                </div>

                <button type="submit" className="w-full inline-flex items-center justify-center h-[52px] px-8 bg-primary hover:bg-primary-hover active:bg-secondary text-white font-semibold rounded-full text-base leading-none shadow-ds transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" data-id="element-1426" data-mp="22dqs38y823mv">
                  Submit Request
                </button>
                <p className="text-xs text-gray-500 leading-relaxed" data-id="element-1427" data-mp="1enwfmshteh35">
                  Requests are free. For access, copy, correction, and deletion
                  requests we'll verify your identity using the email and phone
                  you provide. If we decline a request, you can appeal by
                  choosing "Appeal a decision" above.
                </p>
              </form>
            </>}
        </div>
      </div>
    </main>;
}